import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBan, FaCrown, FaSearch, FaCheck, FaTrash, FaCoins, FaTimes, FaCalendarAlt, FaCalendar } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Avatar from '../../components/UI/Avatar';
import LevelBadge from '../../components/UI/LevelBadge';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

// ── Modal activation Premium (avec plan_type) ────────────────────
function PremiumModal({ user: target, onClose, onDone }) {
  const [planType, setPlanType] = useState('monthly');
  const [saving, setSaving]     = useState(false);

  const COMMISSION = { monthly: 200, yearly: 2000 };
  const PRIX       = { monthly: '1 000 FCFA', yearly: '10 000 FCFA' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/users/${target.id}`, {
        plan: 'premium',
        plan_type: planType,
      });
      toast.success(`${target.pseudo} est maintenant Premium ⭐`);
      onDone(target.id, 'premium');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-sm rounded-2xl p-5"
          style={{ background: '#0d2b1a', border: '1px solid rgba(240,180,41,0.3)' }}
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-qfl-gold font-black uppercase tracking-widest text-sm flex items-center gap-2"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              <FaCrown size={14} /> Activer Premium
            </h3>
            <button onClick={onClose} className="text-white/40 hover:text-white/70"><FaTimes size={15} /></button>
          </div>

          <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <Avatar user={target} size="xs" />
            <p className="text-white font-bold text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {target.pseudo}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <p className="text-white/50 text-xs">Sélectionne le plan payé par l'utilisateur :</p>

            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'monthly', label: 'Mensuel', prix: PRIX.monthly, Icon: FaCalendar },
                { key: 'yearly',  label: 'Annuel',  prix: PRIX.yearly,  Icon: FaCalendarAlt },
              ].map(({ key, label, prix, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPlanType(key)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all"
                  style={{
                    background: planType === key ? 'rgba(240,180,41,0.12)' : 'rgba(255,255,255,0.04)',
                    borderColor: planType === key ? '#f0b429' : 'rgba(255,255,255,0.1)',
                  }}
                >
                  <Icon size={18} style={{ color: planType === key ? '#f0b429' : 'rgba(255,255,255,0.3)' }} />
                  <span className="font-black text-sm" style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    color: planType === key ? '#f0b429' : 'rgba(255,255,255,0.5)',
                  }}>{label}</span>
                  <span className="text-xs font-bold" style={{ color: planType === key ? '#f0b429' : 'rgba(255,255,255,0.3)' }}>
                    {prix}
                  </span>
                </button>
              ))}
            </div>

            {target.referred_by_pseudo && (
              <div className="text-xs p-2.5 rounded-lg" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <span className="text-green-400 font-bold">Parrain détecté</span>
                <span className="text-white/40"> — </span>
                <span className="text-white/60">{target.referred_by_pseudo} recevra </span>
                <span className="text-green-400 font-bold">{COMMISSION[planType].toLocaleString('fr-CI')} FCFA</span>
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full py-2.5 rounded-xl font-black text-sm uppercase tracking-widest disabled:opacity-50 transition-opacity mt-1"
              style={{ background: 'linear-gradient(135deg, #f0b429, #c97d08)', color: '#0d2b1a', fontFamily: "'Rajdhani', sans-serif" }}
            >
              {saving ? 'Activation...' : `ACTIVER PREMIUM ${planType === 'yearly' ? 'ANNUEL' : 'MENSUEL'}`}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Modal don de pièces ───────────────────────────────────────────
function CoinsModal({ user: target, onClose, onDone }) {
  const [amount, setAmount]   = useState('');
  const [reason, setReason]   = useState('');
  const [saving, setSaving]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parseInt(amount);
    if (!parsed || parsed === 0) { toast.error('Montant invalide'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/coins`, { amount: parsed, reason });
      toast.success(data.message);
      onDone(target.id, data.coins);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-sm rounded-2xl p-5"
          style={{ background: '#0d2b1a', border: '1px solid rgba(240,180,41,0.3)' }}
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-qfl-gold font-black uppercase tracking-widest text-sm flex items-center gap-2"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              <FaCoins size={14} /> Donner des pièces
            </h3>
            <button onClick={onClose} className="text-white/40 hover:text-white/70"><FaTimes size={15} /></button>
          </div>

          <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <Avatar user={target} size="xs" />
            <div>
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{target.pseudo}</p>
              <p className="text-white/40 text-xs">{target.coins ?? 0} pièces actuellement</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div>
              <label className="text-white/60 text-xs mb-1 block">
                Montant <span className="text-white/30">(positif pour donner, négatif pour retirer)</span>
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ex : 500 ou -100"
                className="form-input w-full text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1 block">Raison <span className="text-white/30">(optionnel)</span></label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex : Victoire tournoi, compensation..."
                className="form-input w-full text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={saving || !amount}
              className="w-full py-2.5 rounded-xl font-black text-sm uppercase tracking-widest disabled:opacity-50 transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #f0b429, #c97d08)', color: '#0d2b1a', fontFamily: "'Rajdhani', sans-serif" }}
            >
              {saving ? 'Envoi...' : 'CONFIRMER'}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterPeriode, setFilterPeriode] = useState('');
  const [coinsTarget, setCoinsTarget]     = useState(null);
  const [premiumTarget, setPremiumTarget] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const load = () => {
    setLoading(true);
    api.get('/admin/users?limit=200')
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleBan = async (userId, isBanned) => {
    const action = isBanned ? 'débannir' : 'bannir';
    if (!window.confirm(`Voulez-vous ${action} cet utilisateur ?`)) return;
    try {
      await api.put(`/admin/users/${userId}`, { is_banned: !isBanned });
      setUsers((us) => us.map((u) => u.id === userId ? { ...u, is_banned: !isBanned } : u));
      toast.success(`Utilisateur ${action === 'bannir' ? 'banni' : 'débanni'}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    }
  };

  const handlePlanDowngrade = async (userId) => {
    if (!window.confirm('Rétrograder cet utilisateur en Free ?')) return;
    try {
      await api.put(`/admin/users/${userId}`, { plan: 'free' });
      setUsers((us) => us.map((u) => u.id === userId ? { ...u, plan: 'free' } : u));
      toast.success('Plan mis à jour : Free');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    }
  };

  const handlePlanUpdated = (userId, newPlan) => {
    setUsers((us) => us.map((u) => u.id === userId ? { ...u, plan: newPlan } : u));
  };

  const handleCoinsUpdate = (userId, newCoins) => {
    setUsers((us) => us.map((u) => u.id === userId ? { ...u, coins: newCoins } : u));
  };

  const handleDelete = async (userId, pseudo) => {
    if (!window.confirm(`Supprimer définitivement le compte "${pseudo}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers((us) => us.filter((u) => u.id !== userId));
      toast.success(`Compte "${pseudo}" supprimé`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    }
  };

  const periodeStart = (() => {
    const now = new Date();
    if (filterPeriode === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (filterPeriode === '7d')    return new Date(now - 7 * 86400000);
    if (filterPeriode === '30d')   return new Date(now - 30 * 86400000);
    if (filterPeriode === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
    return null;
  })();

  const formatTemps = (minutes) => {
    if (!minutes) return '—';
    if (minutes < 60) return `${minutes}min`;
    return `${Math.floor(minutes / 60)}h${minutes % 60 > 0 ? ` ${minutes % 60}min` : ''}`;
  };

  const filtered = users
    .filter((u) => !filterPlan || u.plan === filterPlan)
    .filter((u) => !periodeStart || new Date(u.created_at) >= periodeStart)
    .filter((u) =>
      !search ||
      u.pseudo?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
    );

  const paginated   = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages  = Math.ceil(filtered.length / PER_PAGE);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const formatTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col gap-4">
      {coinsTarget && (
        <CoinsModal
          user={coinsTarget}
          onClose={() => setCoinsTarget(null)}
          onDone={handleCoinsUpdate}
        />
      )}
      {premiumTarget && (
        <PremiumModal
          user={premiumTarget}
          onClose={() => setPremiumTarget(null)}
          onDone={handlePlanUpdated}
        />
      )}
      <div>
        <h1 className="text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Utilisateurs
        </h1>
        <p className="text-white/40 text-sm">{users.length} utilisateurs inscrits</p>
      </div>

      {/* Filtres */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <FaSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            placeholder="Rechercher pseudo ou email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="form-input pl-9 py-2 text-sm"
          />
        </div>
        <select
          value={filterPlan}
          onChange={(e) => { setFilterPlan(e.target.value); setPage(1); }}
          className="form-input py-2 text-sm"
        >
          <option value="">Tous les plans</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
        </select>
        <select
          value={filterPeriode}
          onChange={(e) => { setFilterPeriode(e.target.value); setPage(1); }}
          className="form-input py-2 text-sm"
        >
          <option value="">Toutes les périodes</option>
          <option value="today">Aujourd'hui</option>
          <option value="7d">7 derniers jours</option>
          <option value="30d">30 derniers jours</option>
          <option value="month">Ce mois-ci</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-green-900">
            <table className="w-full text-sm min-w-[650px]">
              <thead>
                <tr className="border-b border-green-900 bg-qfl-card">
                  {['Utilisateur', 'Email', 'Plan', 'Niveau', 'Parties', 'Temps', 'Inscrit le', 'Actions'].map((h) => (
                    <th key={h} className="text-left px-3 py-3 text-white/60 font-bold uppercase tracking-wider text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((user, i) => {
                  const isAdmin   = user.role === 'admin';
                  const isMe      = user.id === me?.id;

                  return (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className={`border-b border-green-900/50 hover:bg-white/5 transition-colors ${user.is_banned ? 'opacity-50' : ''}`}
                    >
                      {/* Utilisateur */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar user={user} size="xs" />
                          <div>
                            <div className="text-white font-bold text-sm flex items-center gap-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                              {user.pseudo}
                              {isAdmin && <span className="text-xs text-qfl-gold">(Admin)</span>}
                              {user.is_banned && <span className="text-xs text-red-400">(Banni)</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-3 py-3 text-white/50 text-xs">{user.email}</td>

                      {/* Plan */}
                      <td className="px-3 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          user.plan === 'premium'
                            ? 'bg-qfl-gold/20 text-qfl-gold border border-qfl-gold/30'
                            : 'bg-white/10 text-white/50 border border-white/20'
                        }`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                          {user.plan === 'premium' ? '⭐ Premium' : 'Free'}
                        </span>
                      </td>

                      {/* Niveau */}
                      <td className="px-3 py-3">
                        <LevelBadge level={user.level || 1} size="xs" />
                      </td>

                      {/* Parties */}
                      <td className="px-3 py-3 text-white/60 font-semibold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {user.parties_jouees || 0}
                      </td>

                      {/* Temps */}
                      <td className="px-3 py-3 text-white/50 text-xs font-semibold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {formatTemps(user.temps_minutes)}
                      </td>

                      {/* Date */}
                      <td className="px-3 py-3 text-white/40 text-xs">
                        <div>{formatDate(user.created_at)}</div>
                        <div style={{ color: 'rgba(255,255,255,0.25)' }}>{formatTime(user.created_at)}</div>
                      </td>

                      {/* Actions — désactivées pour les admins */}
                      <td className="px-3 py-3">
                        {isAdmin ? (
                          <span className="text-white/20 text-xs italic">—</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            {/* Donner des pièces */}
                            <button
                              onClick={() => setCoinsTarget(user)}
                              className="text-white/30 hover:text-qfl-gold transition-colors"
                              title="Donner / retirer des pièces"
                            >
                              <FaCoins size={14} />
                            </button>

                            {/* Premium toggle */}
                            <button
                              onClick={() => user.plan === 'premium'
                                ? handlePlanDowngrade(user.id)
                                : setPremiumTarget(user)
                              }
                              className={`transition-colors ${user.plan === 'premium' ? 'text-qfl-gold hover:text-yellow-300' : 'text-white/30 hover:text-qfl-gold'}`}
                              title={user.plan === 'premium' ? 'Rétrograder en Free' : 'Activer Premium'}
                            >
                              <FaCrown size={14} />
                            </button>

                            {/* Ban/unban */}
                            <button
                              onClick={() => handleBan(user.id, user.is_banned)}
                              className={`transition-colors ${user.is_banned ? 'text-green-400 hover:text-green-300' : 'text-white/30 hover:text-red-400'}`}
                              title={user.is_banned ? 'Débannir' : 'Bannir'}
                            >
                              {user.is_banned ? <FaCheck size={14} /> : <FaBan size={14} />}
                            </button>

                            {/* Supprimer */}
                            {!isMe && (
                              <button
                                onClick={() => handleDelete(user.id, user.pseudo)}
                                className="text-white/20 hover:text-red-500 transition-colors"
                                title="Supprimer le compte"
                              >
                                <FaTrash size={13} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-white/40 text-xs">{filtered.length} utilisateurs · Page {page}/{totalPages}</span>
              <div className="flex gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 rounded-lg bg-white/10 text-white/60 text-xs disabled:opacity-30 hover:bg-white/20 transition-colors"
                >
                  Préc.
                </button>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg bg-white/10 text-white/60 text-xs disabled:opacity-30 hover:bg-white/20 transition-colors"
                >
                  Suiv.
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminUsers;
